import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  fetchMarketingAutomationsRequest,
  fetchMarketingAutomationsSuccess,
  fetchMarketingAutomationsFailure,
  searchMarketingAutomationsRequest,
  searchMarketingAutomationsSuccess,
  searchMarketingAutomationsFailure,
  fetchMarketingAutomationDetailsRequest,
  fetchMarketingAutomationDetailsSuccess,
  fetchMarketingAutomationDetailsFailure,
  createMarketingAutomationRequest,
  createMarketingAutomationSuccess,
  createMarketingAutomationFailure,
  updateMarketingAutomationRequest,
  updateMarketingAutomationSuccess,
  updateMarketingAutomationFailure,
  deleteMarketingAutomationRequest,
  deleteMarketingAutomationSuccess,
  deleteMarketingAutomationFailure,
} from '../slices/marketingAutomation.slice';

import {
  getMarketingAutomations,
  getMarketingAutomationById,
  createMarketingAutomation,
  updateMarketingAutomation,
  deleteMarketingAutomation,
  searchMarketingAutomations,
} from '@/services/marketingService';

import { 
  CreateMarketingAutomationDto,
  UpdateMarketingAutomationDto,
  FetchMarketingAutomationsOptions 
} from '@/types/models/marketing';
import { toApiError } from '@/lib/errorHelpers';

function* fetchMarketingAutomationsSaga(action: PayloadAction<FetchMarketingAutomationsOptions>): Generator<any, void, any> {
  try {
    const response: any = yield call(getMarketingAutomations, action.payload);
    yield put(fetchMarketingAutomationsSuccess({
      data: response.data || response,
      pagination: response.pagination || {
        page: action.payload.page || 1,
        total: response.length || 0,
        pageSize: action.payload.page_size || 10,
        totalPages: Math.ceil((response.length || 0) / (action.payload.page_size || 10)),
      }
    }));
  } catch (error: any) {
    yield put(fetchMarketingAutomationsFailure(toApiError(error)));
  }
}

function* searchMarketingAutomationsSaga(action: PayloadAction<{ query: string; options?: FetchMarketingAutomationsOptions }>): Generator<any, void, any> {
  try {
    const { query, options = {} } = action.payload;
    const response: any = yield call(searchMarketingAutomations, { query, ...options });
    yield put(searchMarketingAutomationsSuccess({
      data: response.data || response,
      pagination: response.pagination || {
        page: options.page || 1,
        total: response.length || 0,
        pageSize: options.page_size || 10,
        totalPages: Math.ceil((response.length || 0) / (options.page_size || 10)),
      }
    }));
  } catch (error: any) {
    yield put(searchMarketingAutomationsFailure(toApiError(error)));
  }
}

function* fetchMarketingAutomationDetailsSaga(action: PayloadAction<{ id: string }>): Generator<any, void, any> {
  try {
    const response: any = yield call(getMarketingAutomationById, action.payload.id);
    yield put(fetchMarketingAutomationDetailsSuccess(response));
  } catch (error: any) {
    yield put(fetchMarketingAutomationDetailsFailure(toApiError(error)));
  }
}

function* createMarketingAutomationSaga(action: PayloadAction<CreateMarketingAutomationDto>): Generator<any, void, any> {
  try {
  const response: any = yield call(createMarketingAutomation as any, action.payload);
    yield put(createMarketingAutomationSuccess(response));
  } catch (error: any) {
    yield put(createMarketingAutomationFailure(toApiError(error)));
  }
}

function* updateMarketingAutomationSaga(action: PayloadAction<{ id: string; data: UpdateMarketingAutomationDto }>): Generator<any, void, any> {
  try {
    const { id, data } = action.payload;
  const response: any = yield call(updateMarketingAutomation as any, id, data);
    yield put(updateMarketingAutomationSuccess(response));
  } catch (error: any) {
    yield put(updateMarketingAutomationFailure(toApiError(error)));
  }
}

function* deleteMarketingAutomationSaga(action: PayloadAction<{ id: string }>): Generator<any, void, any> {
  try {
    yield call(deleteMarketingAutomation, action.payload.id);
    yield put(deleteMarketingAutomationSuccess({ id: action.payload.id }));
  } catch (error: any) {
    yield put(deleteMarketingAutomationFailure(toApiError(error)));
  }
}

export function* marketingAutomationSaga(): Generator<any, void, any> {
  yield takeLatest(fetchMarketingAutomationsRequest.type, fetchMarketingAutomationsSaga);
  yield takeLatest(searchMarketingAutomationsRequest.type, searchMarketingAutomationsSaga);
  yield takeLatest(fetchMarketingAutomationDetailsRequest.type, fetchMarketingAutomationDetailsSaga);
  yield takeLatest(createMarketingAutomationRequest.type, createMarketingAutomationSaga);
  yield takeLatest(updateMarketingAutomationRequest.type, updateMarketingAutomationSaga);
  yield takeLatest(deleteMarketingAutomationRequest.type, deleteMarketingAutomationSaga);
}

export default marketingAutomationSaga;